import { ShieldCheck, Zap } from "lucide-react";

import { cx } from "@tripcart/traveler-ui/cx";

import type { PluralTranslator, Translator } from "@/lib/translations";

import {
  ALMOST_SOLD_OUT_AT,
  FILLING_UP_AT,
} from "../_packages/_availability-pill";
import type { DepartureStatus } from "./departure-status";
import styles from "./status-pill.module.css";

/**
 * The guide's `.pl` — one status pill per departure. It already carries the
 * seat count when that matters, so nothing else on the card repeats it.
 *
 * The phone takes the mobile guide's `.pl` — 20px, 10.5px, a 5px corner and
 * sentence case. Uppercase at 11px with 0.08em tracking makes "12 SEATS
 * AVAILABLE" 180px wide, which on a 358px card is half the row and reads
 * louder than the date above it. The WORDING stays ours (the guide says only
 * "Available"); it is the only part of this pill that is more useful.
 *
 * `.pl-crit` / `.pl-warn` carry a dot separating the status from the count, so
 * they get a little word-spacing (`counted`) to keep it from crowding either
 * side.
 */
function Pill({
  tone,
  counted = false,
  children,
}: {
  tone: Tone;
  counted?: boolean;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cx(styles.pill, counted && styles.counted)}
      data-tone={tone}
    >
      {children}
    </span>
  );
}

/** Mirrors `_availability-pill.ts` exactly — same tone for the same band, so
 *  the two surfaces cannot drift in colour any more than in wording.
 *
 *  These are the guide's own pairs:
 *    `.pl-warn` → `--warn-bg-mid` + `--warn-fg-dark`  (#fdebc8 / #92400e)
 *    `.pl-crit` → `--danger-bg`   + `--danger-fg`     (#fef2f2 / #b91c1c)
 *
 *  Both were briefly wrong here. `warn` used the LIGHTER warning fill, and
 *  `crit` was moved onto the warning family on the reasoning that running low
 *  is an invitation to hurry rather than a failure. That reasoning is fine and
 *  the guide simply does not agree: it escalates ≤2 into the danger family, and
 *  the two bands read as two different things because of it. The guide wins.
 *
 *  `warn` takes the deeper amber fill with the deeper amber ink — the standard
 *  warning ink is under the floor on the mid fill for this 11px bold label.
 *  Each tone is a `data-tone` rule in `status-pill.module.css`. */
type Tone = "ok" | "warn" | "crit" | "out";

/**
 * Renders a departure's availability, and nothing it does not know.
 *
 * The state that is easy to get wrong: `uncapped` reads "Available" with NO
 * number, because there is no number — `spotsLeft` is null. Printing a large
 * figure here would be inventing one.
 *
 * There used to be a "Checking availability" arm for an `unknown` status, on
 * the correct reasoning that a pending request must never read as sold out.
 * Nothing could reach it — `DepartureDto` carries no liveness field, so
 * `resolveDepartureStatus` never returns it. That distinction is enforced
 * where it can be: `DeparturesSection` shows "Checking which dates are still
 * open…" for the whole section while `departures` is null, and no row renders
 * at all. See `departure-status.ts`.
 */
export function StatusPill({
  status,
  tn,
  t,
}: {
  status: DepartureStatus;
  tn: PluralTranslator;
  t: Translator;
}) {
  switch (status.kind) {
    case "sold-out":
      // "Fully Booked" is the package cards' wording for the same fact. Two
      // words for one state, on one page, is how a reader ends up wondering
      // whether they mean different things.
      return (
        <Pill tone="out">
          {status.reason ? status.reason : t("Fully Booked")}
        </Pill>
      );
    case "seats":
      // The guide's two scarcity bands, and the plain count above them —
      // IDENTICAL wording and IDENTICAL thresholds to the package card, because
      // both read the same two constants from `_availability-pill.ts`.
      //
      // This said "No band, no urgency wording" until 2026-09-15, while the
      // package card twenty pixels below it had already gained the bands. So a
      // date with four seats read "4 seats available" here and "Filling up
      // fast · 4 seats left" there, on one screen — the exact contradiction
      // BOTH files' docstrings claimed to be preventing.
      //
      // The band is a presentation choice made from `spotsLeft`, so
      // `resolveDepartureStatus` stays i18n-free and testable without a
      // translator.
      if (status.spotsLeft <= ALMOST_SOLD_OUT_AT) {
        return (
          <Pill tone="crit" counted>
            {tn(
              "Almost sold out · 1 spot left",
              "Almost sold out · {n} spots left",
              status.spotsLeft,
              { n: status.spotsLeft },
            )}
          </Pill>
        );
      }
      if (status.spotsLeft <= FILLING_UP_AT) {
        return (
          <Pill tone="warn" counted>
            {tn(
              "Filling up fast · 1 spot left",
              "Filling up fast · {n} spots left",
              status.spotsLeft,
              { n: status.spotsLeft },
            )}
          </Pill>
        );
      }
      // Above the bands: "Available", with NO count — the same string the
      // uncapped arm below renders, because to a traveller "plenty of seats"
      // and "no cap" say the identical thing.
      //
      // This printed the raw number ("231 seats available") until 2026-09-21,
      // on a comment claiming the count was "the only part of this pill that is
      // more useful" than the guide. The guide states the opposite in its own
      // source, beside the branch it belongs to: "A spot count only earns its
      // space once the date is running out; on an open date the number is
      // noise." It also published total inventory on every healthy date, which
      // is the operator's business rather than the shopper's.
      //
      // The two bands below five keep their counts. That is the whole point of
      // the rule — the number is what makes "4 spots left" actionable, and what
      // makes "231 seats available" filler.
      return <Pill tone="ok">{t("Available")}</Pill>;
    case "uncapped":
      return <Pill tone="ok">{t("Available")}</Pill>;
  }
}

/** The guide's `.gb` — a promise badge.
 *
 *  Two shapes, because the two guides draw two. On DESKTOP these straddle the
 *  card's top edge as floating pills, so they read as claims about the
 *  departure rather than as more status — the border and the drop are what lift
 *  them off the edge they sit on.
 *
 *  On a PHONE they are plain coloured TEXT inside a ribbon band (mobile guide
 *  `.gb`), because the band already separates them from the card's content. A
 *  bordered, shadowed pill floating over a 358px card's corner is the "sticker"
 *  look — it reads as something stuck onto the card rather than part of it.
 *  Both shapes are `.badge` in `status-pill.module.css`. */

/**
 * The ribbon of promises on an open departure.
 *
 * NOT rendered for a sold-out date: it has no promise to make, and the card's
 * own layout reclaims the ribbon's space when there is none (`hasRibbon`).
 *
 * Both badges are claims a traveller books flights around, so each is gated on
 * a fact rather than emitted with the layout:
 *
 *  - "Guaranteed to run" — `isGuaranteedToRun`, which reads the departure's own
 *    booked count (`capacity − spotsLeft`) against the tour's minimum party.
 *    False whenever any input is missing; see that function for each case.
 *  - "Instant confirmation" — gated on `instantConfirmation`, because a tour
 *    sold ON REQUEST is by definition not confirmed instantly: the operator
 *    answers first. It shipped unconditionally until 2026-09-15, because the
 *    guide emits it for every open departure and it came across with the
 *    layout.
 *
 * Note what this does NOT claim: that "Instant confirmation" is fully backed. A
 * tour whose gateway defers settlement does not confirm instantly either, and
 * nothing in our model says so. Wire that the moment such a field exists.
 *
 * The ribbon renders when EITHER badge has something to say, so a guaranteed
 * on-request departure still gets its green line. `hasRibbon` on the card must
 * stay in step with this or the card pads for a band that never arrives.
 */
export function PromiseBadges({
  status,
  guaranteed,
  instantConfirmation,
  t,
}: {
  status: DepartureStatus;
  /** `isGuaranteedToRun` for this row — enough travellers have already booked
   *  to meet the tour's minimum party. */
  guaranteed: boolean;
  /** False for an on-request tour. */
  instantConfirmation: boolean;
  t: Translator;
}) {
  if (status.kind === "sold-out") return null;
  if (!guaranteed && !instantConfirmation) return null;
  return (
    // Guide `.gb-ribbon` on the phone: a full-width band on `--sunk` with a
    // hairline under it, IN FLOW at the top of the card. The absolute sticker
    // is the desktop shape and comes back at `sm`.
    <span className={styles.ribbon}>
      {/* FIRST, as the guide orders them. It is the stronger promise — whether
          the trip departs at all outranks how fast the booking is confirmed —
          and on a 358px band the two wrap, so the order decides which one
          survives on the first line. */}
      {guaranteed ? (
        <span className={styles.badge} data-tone="guaranteed">
          <ShieldCheck strokeWidth={2.2} />
          {t("Guaranteed to run")}
        </span>
      ) : null}
      {instantConfirmation ? (
        <span className={styles.badge} data-tone="instant">
          <Zap strokeWidth={2.2} />
          {t("Instant confirmation")}
        </span>
      ) : null}
    </span>
  );
}
